<template>
  <div class="condition-modal">
    <form @submit.prevent="handleSubmit">
      <div class="condition-modal__body">
        <div class="condition-modal__type">
          <RadioGroup
            id="condition-type"
            :options="[
              { label: 'Show if', value: 'show' },
              { label: 'Hide if', value: 'hide' },
            ]"
            v-model="conditionForm.type.binding"
          />
        </div>
        <div class="condition-modal__groups">
          <template v-for="(group, groupIndex) in conditionForm.groups.binding" :key="groupIndex">
            <ConditionGroup
              :group="group"
              :groupIndex="groupIndex"
              :items="availableElements"
              @addRule="addRule"
              @removeRule="removeRule"
              @addGroup="addGroup"
              @removeGroup="removeGroup"
            />
            <div
              v-if="conditionForm.groups.binding.length > 0"
              class="condition-modal__group-logic"
            >
              <template v-if="groupIndex === 0 && conditionForm.groups.binding.length > 1">
                <SelectInput
                  id="between-group-logic"
                  :options="[
                    { label: 'And', value: 'AND' },
                    { label: 'Or', value: 'OR' },
                  ]"
                  v-model="conditionForm.betweenGroupLogic.binding"
                  class="p-0 logic-selector"
                />
              </template>
              <template v-else-if="groupIndex < conditionForm.groups.binding.length - 1">
                <span class="condition-modal__group-logic-text">
                  {{
                    conditionForm.betweenGroupLogic.binding.charAt(0).toUpperCase() +
                    conditionForm.betweenGroupLogic.binding.slice(1).toLowerCase()
                  }}
                </span>
              </template>
            </div>
          </template>
          <div class="condition-modal__summary">
            <div class="condition-summary">
              <span class="condition-summary__type">
                <BaseIcon name="filter" size="16" />
                {{
                  conditionForm.type.binding.charAt(0).toUpperCase() +
                  conditionForm.type.binding.slice(1)
                }}
                element if...
              </span>
              <span
                v-for="(group, groupIndex) in conditionForm.groups.binding"
                :key="groupIndex"
                class="condition-summary__group"
              >
                (
                <template v-for="(rule, ruleIndex) in group.rules" :key="ruleIndex">
                  <template v-if="!rule.elementId.binding">
                    <span>not selected</span>
                  </template>
                  <template v-else>
                    {{
                      availableElements.find((el) => el.id === rule.elementId.binding)?.label ||
                      rule.elementId.binding
                    }}
                  </template>
                  {{ rule.operation.binding }}
                  <span v-if="isValueInputRequired(rule.operation.binding) && !rule.value.binding">
                    none
                  </span>
                  <span v-else-if="isValueInputRequired(rule.operation.binding)">
                    {{
                      availableElements
                        .find((el) => el.id === rule.elementId.binding)
                        ?.choices?.find((choice) => choice.id === rule.value.binding)?.content ||
                      rule.value.binding
                    }}
                  </span>
                  <span v-if="ruleIndex < group.rules.length - 1" class="condition-summary__logic">
                    {{ group.logic.binding }}
                  </span>
                </template>
                )
                <span
                  v-if="groupIndex < conditionForm.groups.binding.length - 1"
                  class="condition-summary__logic"
                >
                  {{ conditionForm.betweenGroupLogic.binding }}
                </span>
              </span>
            </div>
          </div>
        </div>
        <div class="condition-modal__actions">
          <Btn icon="layer-plus" variant="tertiary" @click="addGroup()" type="button">
            Add group
          </Btn>
          <Btn variant="secondary" @click.prevent="handleSubmit" :loading="isLoading">
            Save conditions
          </Btn>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
  import { ref, computed, watch } from 'vue';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import RadioGroup from '@/components/inputs/RadioGroup.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import ConditionGroup from '@/components/form/settings/ConditionGroup.vue';
  import { useUIStore } from '@/store/uiStore';
  import { useFormStore } from '@/store/formStore';
  import { validateInput } from '@/utils/validation';
  import { wrapConditions, unwrapConditions } from '@/utils/conditionBindings';

  export default {
    name: 'ConditionSettings',
    components: {
      Btn,
      BaseIcon,
      RadioGroup,
      SelectInput,
      ConditionGroup,
    },
    props: {
      conditions: {
        type: Object,
        required: true,
        default: () => ({
          type: 'show',
          betweenGroupLogic: 'AND',
          groups: [],
        }),
      },
      items: {
        type: Array,
        default: null,
        description:
          'Optional override for the selectable elements. When omitted, falls back to the current form builder elements.',
      },
      onSubmit: {
        type: Function,
        required: true,
      },
    },
    setup(props, { emit }) {
      const uiStore = useUIStore();
      const formStore = useFormStore();
      const isLoading = ref(false);
      const conditionForm = ref(wrapConditions(props.conditions));
      const programmaticUpdate = ref(false);

      const availableElements = computed(() => {
        if (props.items) return props.items;

        return formStore
          .getAllElements()
          .filter(
            (element) =>
              element.type !== 'heading' &&
              element.type !== 'text' &&
              element.id !== formStore.activeElement?.id
          )
          .map((element) => ({
            id: element.id,
            label: element.props?.label || `Field ${element.id}`,
            type: element.type,
            choices: element.type.includes('choice') ? element.props?.choices : null,
          }));
      });

      watch(
        () => props.conditions,
        (newConditions) => {
          if (newConditions) {
            conditionForm.value = wrapConditions(newConditions);
            // Add a default group if no groups are provided
            if (conditionForm.value.groups.binding.length === 0) {
              conditionForm.value.groups.binding.push({
                logic: {
                  binding: 'AND',
                  error: '',
                  rules: ['required'],
                },
                rules: [
                  {
                    elementId: {
                      binding: '',
                      error: '',
                      rules: ['required'],
                    },
                    operation: {
                      binding: 'equals',
                      error: '',
                      rules: ['required'],
                    },
                    value: {
                      binding: '',
                      error: '',
                      rules: ['required'],
                    },
                  },
                ],
              });
            }
          }
        },
        { immediate: true }
      );

      const addGroup = (parentGroupIndex = null) => {
        const newGroup = {
          logic: {
            binding: 'AND',
            error: '',
            rules: ['required'],
          },
          rules: [
            {
              elementId: {
                binding: '',
                error: '',
                rules: ['required'],
              },
              operation: {
                binding: 'equals',
                error: '',
                rules: ['required'],
              },
              value: {
                binding: '',
                error: '',
                rules: ['required'],
              },
            },
          ],
          subGroups: [],
        };
        if (parentGroupIndex === null) {
          conditionForm.value.groups.binding.push(newGroup);
        } else {
          conditionForm.value.groups.binding[parentGroupIndex].subGroups.push(newGroup);
        }
      };

      const removeGroup = (groupIndex, parentGroupIndex = null) => {
        if (parentGroupIndex === null) {
          conditionForm.value.groups.binding.splice(groupIndex, 1);
        } else {
          conditionForm.value.groups.binding[parentGroupIndex].subGroups.splice(groupIndex, 1);
        }
      };

      const addRule = (groupIndex) => {
        programmaticUpdate.value = true;
        conditionForm.value.groups.binding[groupIndex].rules.push({
          elementId: {
            binding: '',
            error: '',
            rules: ['required'],
          },
          operation: {
            binding: 'equals',
            error: '',
            rules: ['required'],
          },
          value: {
            binding: '',
            error: '',
            rules: ['required'],
          },
        });
        programmaticUpdate.value = false;
      };

      const removeRule = (groupIndex, ruleIndex) => {
        conditionForm.value.groups.binding[groupIndex].rules.splice(ruleIndex, 1);
      };

      const validateForm = () => {
        let isValid = true;

        // Validate type
        conditionForm.value.type.error = validateInput(
          conditionForm.value.type.binding,
          conditionForm.value.type.rules
        );
        if (conditionForm.value.type.error) isValid = false;

        // Validate betweenGroupLogic
        conditionForm.value.betweenGroupLogic.error = validateInput(
          conditionForm.value.betweenGroupLogic.binding,
          conditionForm.value.betweenGroupLogic.rules
        );
        if (conditionForm.value.betweenGroupLogic.error) isValid = false;

        // Validate each group
        conditionForm.value.groups.binding.forEach((group) => {
          group.logic.error = validateInput(group.logic.binding, group.logic.rules);
          if (group.logic.error) isValid = false;

          // Validate each rule within the group
          group.rules.forEach((rule) => {
            Object.keys(rule).forEach((key) => {
              if (key === 'value' && !isValueInputRequired(rule.operation.binding)) {
                rule[key].error = '';
                return;
              }

              rule[key].error = validateInput(rule[key].binding, rule[key].rules);
              if (rule[key].error) isValid = false;
            });
          });
        });

        return isValid;
      };

      const handleSubmit = async () => {
        isLoading.value = true;
        const allFieldsValid = validateForm();

        if (allFieldsValid) {
          props.onSubmit(unwrapConditions(conditionForm.value));
        } else {
          const errors = {};
          if (conditionForm.value.type.error) {
            errors.type = conditionForm.value.type.error;
          }
          if (conditionForm.value.betweenGroupLogic.error) {
            errors.betweenGroupLogic = conditionForm.value.betweenGroupLogic.error;
          }
          conditionForm.value.groups.binding.forEach((group, groupIndex) => {
            if (group.logic.error) {
              errors[`group_${groupIndex}_logic`] = group.logic.error;
            }
            group.rules.forEach((rule, ruleIndex) => {
              Object.keys(rule).forEach((key) => {
                if (rule[key].error) {
                  errors[`group_${groupIndex}_rule_${ruleIndex}_${key}`] = rule[key].error;
                }
              });
            });
          });
        }
        isLoading.value = false;
      };

      const isValueInputRequired = (operation) => {
        return !['is_empty', 'is_not_empty'].includes(operation);
      };

      return {
        conditionForm,
        availableElements,
        addGroup,
        removeGroup,
        addRule,
        removeRule,
        handleSubmit,
        isValueInputRequired,
      };
    },
  };
</script>

<style scoped lang="scss">
  .condition-modal {
    &__actions {
      margin-top: 1rem;
      display: flex;
      justify-content: space-between;
    }
    &__groups {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
    &__group-logic {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }
    .condition-summary {
      padding: 0.5rem;
      border-radius: 0.5rem;
      background-color: var(--brand-50);
      border: 1px solid var(--brand-500);
      color: var(--slate-600);
      &__type {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        font-weight: 600;
      }
      &__logic {
        color: var(--slate-800);
        font-weight: 600;
        margin: 0 0.25rem;
      }
    }
  }
</style>
